import { Injectable } from "@angular/core";
import { Object3DUserData } from "../../../../utils/shape";
import { CountCalculatorService } from "./count-calculator.service";
import { Face, FaceType } from "../../../../utils/count-calculator/count-calculator.model";
import * as THREE from "three";
import { Mesh } from "three";
import { MeshType } from "../../utils/shape-type";
import { FaceMetaDataMap } from "~/src/utils/shape-facetype";
import { SceneManagerService } from "./scene-manager/scene-manager.service";
import { HelperService } from "../shared/helpers/helper.service";
import { ComponentInteractionService } from "./component-interaction/component-interaction.service";
import { InteractionService } from "../shared/helpers/interaction.service";
import { Action, UndoRedoAction } from "../shared/models/undoRedo.model";
import { UndoRedoService } from "./undoRedo/undo-redo.service";
import { MeshVisualService } from "./designer/mesh-visual.service";

@Injectable({
    providedIn: "root",
})
export class InvalidShapeService {
    public cellReferenceForShapes!: { row: number; column: number; cell: number };
    public setUuidIsGround = new Set<String>();
    private meshes: Mesh[] = [];

    constructor(
        private sceneManagerService: SceneManagerService,
        private helperService: HelperService,
        private componentInteractionService: ComponentInteractionService,
        private interactionService: InteractionService,
        private countService: CountCalculatorService,
        private undoRedoService: UndoRedoService,
        private meshVisualService: MeshVisualService,
    ) {
        this.sceneManagerService.meshes$.subscribe((meshes) => {
            this.meshes = meshes;
        });
    }

    //check the valid or invalid shapes while save/publish
    public async checkInvalid(): Promise<boolean> {
        const meshes: Mesh[] = this.meshes;
        const invalidMeshes: Mesh[] = [];
        for (const mesh of meshes) {
            const isInvalid = await this.invalidCheckShape(mesh, meshes);
            if (isInvalid) {
                this.meshVisualService.markMeshAsInvalid(mesh);
                invalidMeshes.push(mesh);
            } else {
                const previousInvalidShapes = this.componentInteractionService.getInvalidMeshes;
                if (previousInvalidShapes.length) {
                    this.meshVisualService.revertToOriginalColors(mesh);
                }
            }
        }

        this.interactionService.render();
        this.resetIsGroundShapeUuid();

        if (invalidMeshes.length) {
            const validMeshes = meshes.filter((mesh) => !invalidMeshes.includes(mesh));

            this.componentInteractionService.setInvalidMeshes(invalidMeshes);
            this.componentInteractionService.setValidMeshes(validMeshes);

            validMeshes.forEach((mesh) => {
                this.meshVisualService.markMeshAsValid(mesh);
            });

            this.interactionService.render();

            const action: UndoRedoAction = {
                actionType: Action.HandleInvalidJoints,
                meshStates: meshes.map((mesh) => ({
                    mesh: mesh,
                    wasInvalid: invalidMeshes.includes(mesh),
                })),
            };
            this.undoRedoService.addToUndo(action);
            return true;
        }

        this.componentInteractionService.setInvalidMeshes([]);
        this.componentInteractionService.setValidMeshes([]);
        return false;
    }

    public async invalidCheckShape(shape: Mesh, objects: THREE.Object3D[]): Promise<boolean> {
        if (!shape.userData[Object3DUserData.originalColors]) {
            const originalColors: THREE.Color[] = [];

            // Retrieve and store the color for each material
            ((shape as THREE.Mesh).material as THREE.MeshBasicMaterial[]).forEach((material) => {
                originalColors.push(material.color.clone()); // Save the original color
            });
            shape.userData[Object3DUserData.originalColors] = originalColors;
        }

        this.cellReferenceForShapes = shape.userData[Object3DUserData.cellReference];
        const sideInfo = this.sideShapesResult(objects as Mesh[]);
        const { isShapeOnTop, isShapeOnBottom, isShapeOnRight, isShapeOnLeft, isShapeOnFront, isShapeOnBack } =
            sideInfo;

        const occupiedSidesCount = [
            isShapeOnTop,
            isShapeOnBottom,
            isShapeOnRight,
            isShapeOnLeft,
            isShapeOnFront,
            isShapeOnBack,
        ].reduce((count, side) => count + (side ? 1 : 0), 0);

        const isShapeNotAttached = !occupiedSidesCount;
        const faceMetaData = shape.userData[Object3DUserData.faceMetaDataMap] as FaceMetaDataMap;
        let test: Mesh[] = [];
        test.push(shape);
        await this.countService.calculate(test);

        if (typeof faceMetaData === "object") {
            const faceAttachmentValidation = {
                bottom: false,
                top: false,
                back: false,
                front: false,
                left: false,
                right: false,
            };

            type FaceKey = keyof typeof faceAttachmentValidation;

            const faceChecks: {
                key: FaceKey;
                positionKey: string;
                shapeIndex: number;
                adjacentIndex: number;
                condition: boolean;
            }[] = [
                {
                    key: "bottom",
                    positionKey: Face.Bottom,
                    shapeIndex: 5,
                    adjacentIndex: 4,
                    condition: isShapeOnBottom,
                },
                { key: "top", positionKey: Face.Top, shapeIndex: 4, adjacentIndex: 5, condition: isShapeOnTop },
                { key: "left", positionKey: Face.Left, shapeIndex: 3, adjacentIndex: 1, condition: isShapeOnLeft },
                {
                    key: "right",
                    positionKey: Face.Right,
                    shapeIndex: 1,
                    adjacentIndex: 3,
                    condition: isShapeOnRight,
                },
                {
                    key: "front",
                    positionKey: Face.Front,
                    shapeIndex: 0,
                    adjacentIndex: 2,
                    condition: isShapeOnFront,
                },
                { key: "back", positionKey: Face.Back, shapeIndex: 2, adjacentIndex: 0, condition: isShapeOnBack },
            ];

            for (const check of faceChecks) {
                if (check.condition) {
                    faceAttachmentValidation[check.key] = this.checkFaceTypeIsMatch(
                        shape,
                        objects as Mesh[],
                        check.positionKey,
                        check.shapeIndex,
                        check.adjacentIndex,
                    );
                }
            }

            const isValidAllFaces = Object.values(faceAttachmentValidation).reduce(
                (count, side) => count + (side ? 1 : 0),
                0,
            ); // check for all faces is having vaild attachment shape
            if (isValidAllFaces) {
                return !this.validateShapeAttachment(objects as THREE.Mesh[], shape);
            } else {
                return this.cellReferenceForShapes.cell === 0 ? this.isInvalidShapeOnPlane(shape) : true;
            }
        }

        if (isShapeNotAttached) {
            return true;
        } else {
            const originalColors = shape.userData[Object3DUserData.originalColors];
            if (originalColors && Array.isArray(originalColors)) {
                if (shape instanceof THREE.Mesh) {
                    // Check if the shape is a mesh and handle multi-material case
                    if (Array.isArray(shape.material)) {
                        shape.material.forEach((material: THREE.Material, index: number) => {
                            if (material instanceof THREE.MeshBasicMaterial && originalColors[index]) {
                                const changedColor = new THREE.Color(originalColors[index]);
                                material.color.copy(changedColor);
                                material.transparent = false;
                                material.opacity = 1;
                            }
                        });
                    }
                }
            }
        }

        const originalColors = shape.userData[Object3DUserData.originalColors];
        if (originalColors && Array.isArray(originalColors)) {
            if (shape instanceof THREE.Mesh) {
                // Check if the shape is a mesh and handle multi-material case
                if (Array.isArray(shape.material)) {
                    shape.material.forEach((material: THREE.Material, index: number) => {
                        if (material instanceof THREE.MeshBasicMaterial && originalColors[index]) {
                            const changedColor = new THREE.Color(originalColors[index]);
                            material.color.copy(changedColor);
                            material.transparent = false;
                            material.opacity = 1;
                        }
                    });
                }
            }
        }

        return false;
    }

    private checkFaceTypeIsMatch(
        shape: Mesh,
        objects: Mesh[],
        positionKey: string,
        currentShapeIndex: number,
        adjacentIndex: number,
    ): boolean {
        const meshRealFace = shape.userData[Object3DUserData.orientationData][currentShapeIndex] as Face;
        const faceMetaDataMap = shape.userData[Object3DUserData.faceMetaDataMap] as FaceMetaDataMap;
        const faceType = faceMetaDataMap[meshRealFace].type;
        const adjacentUuid = shape.userData[Object3DUserData.adjacentData]?.[positionKey];
        if (!adjacentUuid) return false;
        const adjacentShape = objects.find((shape) => shape.uuid === adjacentUuid);
        if (!adjacentShape) return false;

        const adjacentRealFace = adjacentShape.userData[Object3DUserData.orientationData][adjacentIndex] as Face;
        const adjacentFaceMetaDataMap = adjacentShape.userData[Object3DUserData.faceMetaDataMap] as FaceMetaDataMap;
        const adjacentFaceType = adjacentFaceMetaDataMap[adjacentRealFace].type;
        return (
            (faceType === FaceType.Plane && adjacentFaceType === FaceType.Plane) ||
            (faceType === FaceType.HalfPlane && adjacentFaceType === FaceType.HalfPlane) ||
            (faceType === FaceType.Node && adjacentFaceType === FaceType.Hole) ||
            (faceType === FaceType.Hole && adjacentFaceType === FaceType.Node)
        );
    }

    public get isInvalidShapeOnPlane(): (shape: Mesh) => boolean {
        return (shape: Mesh): boolean => {
            const bottomFace = shape.userData[Object3DUserData.orientationData][5] as Face;
            const faceMetaDataMap = shape.userData[Object3DUserData.faceMetaDataMap] as FaceMetaDataMap;
            const faceType = faceMetaDataMap[bottomFace].type;
            return faceType === FaceType.Curved;
        };
    }

    public validateShapeAttachment(objects: Mesh[], shape: Mesh): boolean {
        const isValid = this.checkNeighborsConnectedToGround(objects, shape);

        if (isValid) {
            return true; // Valid if any neighbor is connected to the ground
        } else {
            return false; // Invalid if no neighbors are connected to the ground
        }
    }

    // Helper to check if any neighboring shape is connected to the ground
    private checkNeighborsConnectedToGround(objects: Mesh[], shape: Mesh): boolean {
        const visited = new Set<Mesh>();
        const isConnectedToGround = this.dfsCheckGround(objects, shape, visited);
        if (isConnectedToGround) {
            this.setGroundShapeUuid(shape.uuid);
        }
        return isConnectedToGround;
    }

    private setGroundShapeUuid(shapeUuid: string): void {
        this.setUuidIsGround.add(shapeUuid);
    }

    private dfsCheckGround(objects: Mesh[], shape: Mesh, visited: Set<Mesh>, depth: number = 0): boolean {
        if (visited.has(shape)) return false;
        visited.add(shape);

        if (this.setUuidIsGround.has(shape.uuid)) return true;

        if (this.isOnGroundAndValid(shape)) {
            return true;
        }

        const neighbors = this.getSideNeighbors(objects, shape);

        for (const neighbor of neighbors) {
            if (this.dfsCheckGround(objects, neighbor, visited, depth + 1)) {
                return true;
            }
        }

        return false;
    }

    // Helper to get neighboring shapes (top, left, right, front, back)

    private getSideNeighbors(objects: Mesh[], shape: Mesh): THREE.Mesh[] {
        let isShapeOnTop = false,
            isShapeOnBottom = false,
            isShapeOnRight = false,
            isShapeOnLeft = false,
            isShapeOnFront = false,
            isShapeOnBack = false;

        const sideMappings: Record<"Top" | "Bottom" | "Right" | "Left" | "Front" | "Back", () => void> = {
            Top: () => (isShapeOnTop = true),
            Bottom: () => (isShapeOnBottom = true),
            Right: () => (isShapeOnRight = true),
            Left: () => (isShapeOnLeft = true),
            Front: () => (isShapeOnFront = true),
            Back: () => (isShapeOnBack = true),
        };

        let neighbors: Mesh[] = [];
        let realFaceSide;
        const adjacent = shape.userData[Object3DUserData.adjacentData];

        for (const [positionKey, adjacentUuid] of Object.entries(adjacent)) {
            sideMappings[positionKey as keyof typeof sideMappings]();
        }

        const faceChecks: {
            positionKey: string;
            shapeIndex: number;
            adjacentIndex: number;
            condition: boolean;
        }[] = [
            { positionKey: "Bottom", shapeIndex: 5, adjacentIndex: 4, condition: isShapeOnBottom },
            { positionKey: "Top", shapeIndex: 4, adjacentIndex: 5, condition: isShapeOnTop },
            { positionKey: "Left", shapeIndex: 3, adjacentIndex: 1, condition: isShapeOnLeft },
            { positionKey: "Right", shapeIndex: 1, adjacentIndex: 3, condition: isShapeOnRight },
            { positionKey: "Front", shapeIndex: 0, adjacentIndex: 2, condition: isShapeOnFront },
            { positionKey: "Back", shapeIndex: 2, adjacentIndex: 0, condition: isShapeOnBack },
        ];

        for (const check of faceChecks) {
            if (check.condition) {
                const isCheck = this.checkFaceTypeIsMatch(
                    shape,
                    objects as Mesh[],
                    check.positionKey,
                    check.shapeIndex,
                    check.adjacentIndex,
                );

                if (isCheck) {
                    const adjacentUuid = adjacent[check.positionKey];
                    const adjacentShape = objects.find((shape) => shape.uuid === adjacentUuid);
                    const isObjectRestrictedShape = [
                        MeshType.RightTriangle,
                        // MeshType.ConcavePrism,
                        MeshType.HalfCylinder,
                        MeshType.CurvedCube,
                    ].includes(adjacentShape!.name as MeshType);

                    if (isObjectRestrictedShape) {
                        switch (check.positionKey) {
                            case Face.Left:
                                realFaceSide = adjacentShape?.userData[Object3DUserData.orientationData][1];
                                break;
                            case Face.Right:
                                realFaceSide = adjacentShape?.userData[Object3DUserData.orientationData][3];
                                break;
                            case Face.Top:
                                realFaceSide = adjacentShape?.userData[Object3DUserData.orientationData][5];
                                break;
                            case Face.Bottom:
                                realFaceSide = adjacentShape?.userData[Object3DUserData.orientationData][4];
                                break;
                            case Face.Front:
                                realFaceSide = adjacentShape?.userData[Object3DUserData.orientationData][2];
                                break;
                            case Face.Back:
                                realFaceSide = adjacentShape?.userData[Object3DUserData.orientationData][0];
                                break;
                            default:
                                break;
                        }
                    }

                    if (isObjectRestrictedShape && (realFaceSide === Face.Right || realFaceSide === Face.Left)) {
                        const shapeOreintation = shape.userData[Object3DUserData.orientationData];
                        const adjacentOrientation = adjacentShape?.userData[Object3DUserData.orientationData];
                        if (
                            shape.name === adjacentShape?.name &&
                            JSON.stringify(shapeOreintation) === JSON.stringify(adjacentOrientation)
                        ) {
                            neighbors.push(adjacentShape as Mesh);
                        }
                    } else {
                        neighbors.push(adjacentShape as Mesh);
                    }
                }
            }
        }
        return neighbors;
    }

    private isOnGroundAndValid(shape: Mesh): boolean {
        const { cell } = shape.userData[Object3DUserData.cellReference];
        return cell === 0 && !this.isInvalidShapeOnPlane(shape);
    }

    // To check  all side of the faces of shape is attached
    public sideShapesResult(objects: Mesh[]): {
        isShapeOnTop: boolean;
        isShapeOnBottom: boolean;
        isShapeOnRight: boolean;
        isShapeOnLeft: boolean;
        isShapeOnFront: boolean;
        isShapeOnBack: boolean;
    } {
        const row = this.cellReferenceForShapes.row;
        const column = this.cellReferenceForShapes.column;
        const cell = this.cellReferenceForShapes.cell;

        let isShapeOnTop = false;
        let isShapeOnBottom = false;
        let isShapeOnRight = false;
        let isShapeOnLeft = false;
        let isShapeOnFront = false;
        let isShapeOnBack = false;

        const foundShapeOnBottom: Mesh[] = [];

        for (const otherShape of objects) {
            const otherCellReference = otherShape.userData[Object3DUserData.cellReference];
            const otherRow = otherCellReference.row;
            const otherColumn = otherCellReference.column;
            const otherCell = otherCellReference.cell;

            if (otherRow === row && otherColumn === column && otherCell === cell + 1) {
                isShapeOnTop = true;
            } else if (otherRow === row && otherColumn === column && otherCell === cell - 1) {
                isShapeOnBottom = true;
                foundShapeOnBottom.push(otherShape);
            } else if (otherRow === row - 1 && otherColumn === column && otherCell === cell) {
                isShapeOnRight = true;
            } else if (otherRow === row + 1 && otherColumn === column && otherCell === cell) {
                isShapeOnLeft = true;
            } else if (otherRow === row && otherColumn === column + 1 && otherCell === cell) {
                isShapeOnFront = true;
            } else if (otherRow === row && otherColumn === column - 1 && otherCell === cell) {
                isShapeOnBack = true;
            }
        }

        return {
            isShapeOnTop,
            isShapeOnBottom,
            isShapeOnRight,
            isShapeOnLeft,
            isShapeOnFront,
            isShapeOnBack,
        };
    }

    // reset ground shape uuid
    public resetIsGroundShapeUuid(): void {
        this.setUuidIsGround.clear();
    }
}
